import { useSyncExternalStore } from 'react';

/**
 * The portal's one place for "that worked / be careful / that failed" messages.
 *
 * A toast is a short, self-dismissing note that appears wherever the person is, so a record that has
 * just moved to another list (an invoice leaving Awaiting invoice for Open invoices, say) is still
 * acknowledged on the screen they are looking at. Success and failure are raised for every write by
 * the API client (see `api/toast-messages.ts`); a screen raises a warning, or a more specific
 * sentence, by calling `toast.*` itself.
 *
 * Plain module state read through `useSyncExternalStore`: no provider to forget to mount, and the
 * API client (not a component) can raise one.
 */
export type ToastKind = 'success' | 'warning' | 'error';

export interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

/** Errors stay a little longer than a success: they usually need reading, a success only needs seeing. */
const LIFETIME_MS: Record<ToastKind, number> = { success: 4000, warning: 7000, error: 8000 };
const MAX_VISIBLE = 4;

let toasts: readonly Toast[] = [];
let nextId = 1;
const listeners = new Set<() => void>();

const emit = () => { listeners.forEach((l) => l()); };

export function dismissToast(id: number): void {
  toasts = toasts.filter((x) => x.id !== id);
  emit();
}

function push(kind: ToastKind, message: string): number {
  // The same sentence twice in a row is one event reported twice (a double click, a retry): show it once.
  const last = toasts[toasts.length - 1];
  if (last && last.kind === kind && last.message === message) return last.id;
  const id = nextId++;
  toasts = [...toasts, { id, kind, message }].slice(-MAX_VISIBLE);
  emit();
  setTimeout(() => { dismissToast(id); }, LIFETIME_MS[kind]);
  return id;
}

export const toast = {
  success: (message: string) => push('success', message),
  warning: (message: string) => push('warning', message),
  error: (message: string) => push('error', message),
};

export function useToasts(): readonly Toast[] {
  return useSyncExternalStore(
    (l) => { listeners.add(l); return () => { listeners.delete(l); }; },
    () => toasts,
    () => toasts,
  );
}

/** For tests only. */
export function clearToasts(): void {
  toasts = [];
  emit();
}
